<template>
  <div class="product-center">
    <!-- 顶部导航组件 -->
    <TheHeader />
    
    <!-- 页面头部区域 -->
    <div class="page-header">
      <div class="header-background">
        <div class="header-content">
          <div class="title-section">
            <h1>产品中心</h1>
            <p>专业品质药品，守护您的健康</p>
            <div class="breadcrumb">
              <router-link to="/">首页</router-link>
              <span class="separator">/</span>
              <span class="current">产品中心</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 筛选和搜索区域 -->
    <div class="filter-section">
      <div class="filter-container">
        <!-- 分类筛选 -->
        <div class="category-section">
          <h3 class="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            产品分类
          </h3>
          <div class="category-tags">
            <div
              class="category-tag"
              :class="{ active: !activeCategoryId }"
              @click="handleCategoryClick(null)"
            >
              <span>全部产品</span>
              <div class="tag-indicator"></div>
            </div>
            <div
              v-for="category in categories"
              :key="category.id"
              class="category-tag"
              :class="{ active: activeCategoryId === category.id }"
              @click="handleCategoryClick(category.id)"
            >
              <span>{{ category.name }}</span>
              <div class="tag-indicator"></div>
            </div>
          </div>
          <!-- 分类加载状态 -->
          <div v-if="categoryLoading" class="category-loading">
            <el-icon class="loading-icon-sm"><Loading /></el-icon>
            <span>加载分类中...</span>
          </div>
        </div>

        <!-- 搜索区域 -->
        <div class="search-section">
          <h3 class="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
            快速搜索
          </h3>
          <div class="search-input-wrapper">
            <input
              type="text"
              v-model="searchKeyword"
              placeholder="输入药品名称、厂家或批准文号搜索"
              @keyup.enter="handleSearch"
              class="search-input"
            />
            <button @click="handleSearch" class="search-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 页面内容区域 -->
    <div class="product-content">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <el-icon class="loading-icon"><Loading /></el-icon>
        <p>正在加载产品数据...</p>
      </div>
      
      <!-- 错误提示 -->
      <div v-else-if="error" class="error-container">
        <el-icon class="error-icon"><WarningFilled /></el-icon>
        <p>{{ error }}</p>
        <button class="retry-btn" @click="loadData">重试</button>
      </div>
      
      <!-- 无数据提示 -->
      <div v-else-if="productList.length === 0" class="empty-container">
        <el-empty description="暂无产品数据" />
      </div>
      
      <!-- 产品列表 -->
      <div v-else class="products-grid">
        <div class="product-item" v-for="product in productList" :key="product.id">
          <div class="product-image-container">
            <img :src="getFullImageUrl(product.image) || placeholderImages.med1" :alt="product.name" />
            <div class="image-overlay">
              <div class="overlay-content">
                <router-link :to="{ name: 'ProductDetail', params: { id: product.id } }" class="quick-view-btn">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                  <span>查看详情</span>
                </router-link>
              </div>
            </div>
            <div class="product-badge">
              <span>{{ getCategoryName(product.category_id) }}</span>
            </div>
          </div>
          <div class="product-content">
            <div class="product-header">
              <h3 class="product-title">{{ product.name }}</h3>
            </div>
            <div class="product-details">
              <div class="detail-item" v-if="product.manufacturer">
                <div class="detail-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <div class="detail-content">
                  <span class="detail-label">生产厂家</span>
                  <span class="detail-value">{{ getShortManufacturer(product.manufacturer) }}</span>
                </div>
              </div>
              <div class="detail-item" v-if="product.approval_number">
                <div class="detail-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                  </svg>
                </div>
                <div class="detail-content">
                  <span class="detail-label">批准文号</span>
                  <span class="detail-value">{{ product.approval_number }}</span>
                </div>
              </div>
              <div class="detail-item description-item">
                <div class="detail-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/>
                  </svg>
                </div>
                <div class="detail-content">
                  <span class="detail-label">适应症</span>
                  <span class="detail-value">{{ getShortDescription(product.description) || (product.indications ? getShortIndications(product.indications) : '暂无描述') }}</span>
                </div>
              </div>
            </div>
            <div class="product-actions">
              <router-link :to="{ name: 'ProductDetail', params: { id: product.id } }" class="action-button">
                了解更多
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                </svg>
              </router-link>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 分页 -->
      <div class="pagination-container" v-if="pagination.total > 0">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="pagination.total"
          :page-size="pagination.pageSize"
          :current-page="pagination.currentPage"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
    
    <!-- 底部信息组件 -->
    <TheFooter />
  </div>
</template>

<script>
// 导入Element Plus组件
import { ElPagination, ElEmpty, ElIcon } from 'element-plus'
import { Loading, WarningFilled } from '@element-plus/icons-vue'
import 'element-plus/dist/index.css'

// 导入占位图片
import { placeholderImages } from '@/assets/placeholder.js'

// 导入自定义组件
import TheHeader from '@/components/TheHeader.vue'
import TheFooter from '@/components/TheFooter.vue'

// 导入API
import { getMedicines, getMedicinesByCategory, getMedicineCategories, searchMedicineAPI, BASE_URL } from '@/api/config'

export default {
  name: 'ProductCenter',
  components: {
    ElPagination,
    ElEmpty,
    ElIcon,
    Loading,
    WarningFilled,
    TheHeader,
    TheFooter
  },
  data() {
    return {
      // 产品相关数据
      productList: [],
      loading: false,
      error: null,
      pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },
      
      // 分类相关
      categories: [],
      categoryLoading: false,
      categoryError: null,
      activeCategoryId: null,
      
      // 搜索相关
      searchKeyword: '',
      
      // 占位图片
      placeholderImages
    }
  },
  methods: {
    // 加载数据
    async loadData() {
      this.loading = true;
      this.error = null;
      
      try {
        let response;
        const { currentPage, pageSize } = this.pagination;
        
        if (this.searchKeyword) {
          // 搜索模式
          response = await searchMedicineAPI(this.searchKeyword, currentPage, pageSize);
        } else if (this.activeCategoryId) {
          // 分类筛选模式
          response = await getMedicinesByCategory(this.activeCategoryId, currentPage, pageSize);
        } else {
          // 默认模式 - 所有产品
          response = await getMedicines({
            page: currentPage,
            pagesize: pageSize
          });
        }
        
        if (response.code === 1) {
          this.productList = response.data.data || [];
          this.pagination.total = response.data.total || 0;
        } else {
          this.error = response.msg || '获取产品数据失败';
        }
      } catch (error) {
        console.error('获取产品数据错误:', error);
        this.error = '网络错误，请稍后重试';
      } finally {
        this.loading = false;
      }
    },
    
    // 加载分类数据
    async loadCategories() {
      this.categoryLoading = true;
      this.categoryError = null;
      
      try {
        const response = await getMedicineCategories();
        
        if (response.code === 1) {
          this.categories = response.data.sort((a, b) => b.weigh - a.weigh);
        } else {
          this.categoryError = response.msg || '获取分类数据失败';
          console.error('获取分类失败:', this.categoryError);
        }
      } catch (error) {
        console.error('获取分类数据错误:', error);
        this.categoryError = '网络错误，请稍后重试';
      } finally {
        this.categoryLoading = false;
      }
    },
    
    // 处理分类点击
    handleCategoryClick(categoryId) {
      if (this.activeCategoryId === categoryId) return;
      
      // 通过URL查询参数来设置分类筛选
      if (categoryId) {
        this.$router.push({
          name: 'Products',
          query: { category: categoryId }
        });
      } else {
        this.$router.push({ name: 'Products' });
      }
      
      // 分类和页码的更新会通过watch处理
    },
    
    // 处理搜索
    handleSearch() {
      if (this.searchKeyword.trim() === '') {
        // 如果搜索关键词为空，重置回默认状态
        if (this.activeCategoryId) {
          // 如果有选中的分类，则保持分类筛选
          this.loadData();
        } else {
          // 否则加载全部数据
          this.loadData();
        }
        return;
      }
      
      // 执行搜索查询
      this.pagination.currentPage = 1;
      this.loadData();
    },
    
    // 处理分页变化
    handleCurrentChange(page) {
      this.pagination.currentPage = page;
      this.loadData();
    },
    
    // 提取短厂商名称
    getShortManufacturer(manufacturer) {
      if (!manufacturer) return '暂无';
      return manufacturer.length > 10 ? manufacturer.substring(0, 10) + '...' : manufacturer;
    },
    
    // 从描述HTML中提取纯文本短描述
    getShortDescription(description) {
      if (!description) return '';
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = description;
      const text = tempDiv.textContent || tempDiv.innerText || '';
      return text.substring(0, 50) + (text.length > 50 ? '...' : '');
    },
    
    // 从适应症HTML中提取纯文本短描述
    getShortIndications(indications) {
      if (!indications) return '';
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = indications;
      const text = tempDiv.textContent || tempDiv.innerText || '';
      return text.substring(0, 50) + (text.length > 50 ? '...' : '');
    },
    
    // 获取完整图片URL
    getFullImageUrl(path) {
      if (!path) return null;
      
      // 如果已经是完整URL则直接返回
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      
      // 否则拼接基础URL
      return `${BASE_URL}${path}`;
    },
    
    // 根据分类ID获取分类名称
    getCategoryName(categoryId) {
      if (!categoryId) return '医药健康';
      const category = this.categories.find(c => c.id === categoryId);
      return category ? category.name : '医药健康';
    }
  },
  created() {
    // 检查URL参数是否包含category
    const categoryParam = this.$route.query.category;
    if (categoryParam) {
      this.activeCategoryId = parseInt(categoryParam, 10);
    }
    
    // 加载分类数据
    this.loadCategories();
    // 加载产品数据
    this.loadData();
  },
  watch: {
    // 监听路由变化，更新分类筛选
    '$route.query.category': function(newCategoryId) {
      if (newCategoryId) {
        this.activeCategoryId = parseInt(newCategoryId, 10);
      } else {
        this.activeCategoryId = null;
      }
      
      // 重置页码并重新加载数据
      this.pagination.currentPage = 1;
      this.loadData();
    }
  }
}
</script>

<style scoped>
.product-center {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  color: #333;
  line-height: 1.6;
  background: linear-gradient(135deg,
    #0f0f23 0%,
    #1a1a3e 25%,
    #2d1b69 50%,
    #1a1a3e 75%,
    #0f0f23 100%);
  background-size: 400% 400%;
  animation: productCenterGradient 20s ease infinite;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}

.product-center::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(236, 72, 153, 0.08) 0%, transparent 50%);
  animation: pulseGlow 12s ease-in-out infinite;
  pointer-events: none;
  z-index: 1;
}

.product-center::after {
  content: '';
  position: fixed;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(from 0deg, transparent, rgba(59, 130, 246, 0.03), transparent, rgba(139, 92, 246, 0.03), transparent);
  animation: rotate 30s linear infinite;
  pointer-events: none;
  z-index: 1;
}

/* 页面头部样式 */
.page-header {
  position: relative;
  margin-top: 80px;
  overflow: hidden;
  z-index: 2;
}

.header-background {
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.95) 0%,
    rgba(139, 92, 246, 0.95) 25%,
    rgba(236, 72, 153, 0.95) 50%,
    rgba(251, 191, 36, 0.95) 75%,
    rgba(34, 197, 94, 0.95) 100%);
  background-size: 400% 400%;
  animation: headerGradient 18s ease infinite;
  position: relative;
  padding: 120px 0 80px;
  backdrop-filter: blur(20px);
}

.header-background::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 70% 30%, rgba(255, 255, 255, 0.08) 0%, transparent 50%);
  animation: pulseGlow 8s ease-in-out infinite;
}

.header-background::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(from 0deg, transparent, rgba(255, 255, 255, 0.05), transparent);
  animation: rotate 20s linear infinite;
  pointer-events: none;
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 10;
}

.title-section {
  text-align: center;
  color: white;
  position: relative;
}

.title-section h1 {
  font-size: 4.5rem;
  font-weight: 900;
  margin-bottom: 20px;
  text-shadow: 0 0 40px rgba(255, 255, 255, 0.3),
               0 8px 16px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.02em;
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0.9) 50%,
    rgba(255, 255, 255, 1) 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: textShine 3s ease-in-out infinite;
}

.title-section p {
  font-size: 1.6rem;
  opacity: 0.95;
  margin-bottom: 30px;
  font-weight: 400;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.5px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 1rem;
  opacity: 0.8;
}

.breadcrumb a {
  color: white;
  text-decoration: none;
  transition: opacity 0.3s ease;
}

.breadcrumb a:hover {
  opacity: 1;
}

.separator {
  opacity: 0.6;
}

.current {
  font-weight: 600;
}

/* 震撼筛选区域样式 */
.filter-section {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15),
              0 0 0 1px rgba(255, 255, 255, 0.4),
              inset 0 1px 0 rgba(255, 255, 255, 0.6);
  position: relative;
  z-index: 10;
  margin-top: -50px;
  border-radius: 30px 30px 0 0;
  border: 2px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
}

.filter-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.02) 0%,
    rgba(139, 92, 246, 0.04) 25%,
    rgba(236, 72, 153, 0.02) 50%,
    rgba(251, 191, 36, 0.04) 75%,
    rgba(34, 197, 94, 0.02) 100%);
  pointer-events: none;
}

.filter-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 50px 20px;
  position: relative;
  z-index: 2;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.4rem;
  font-weight: 800;
  background: linear-gradient(135deg, #2c3e50 0%, #3b82f6 50%, #8b5cf6 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 25px;
  position: relative;
}

.section-title svg {
  color: #3b82f6;
  filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3));
}

/* 现代化分类标签样式 */
.category-section {
  margin-bottom: 50px;
}

.category-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.category-tag {
  position: relative;
  background: rgba(59, 130, 246, 0.1);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(59, 130, 246, 0.2);
  border-radius: 30px;
  padding: 15px 30px;
  cursor: pointer;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  font-weight: 700;
  color: #3b82f6;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.1);
}

.category-tag::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: left 0.6s ease;
}

.category-tag:hover::before {
  left: 100%;
}

.category-tag:hover {
  transform: translateY(-5px) scale(1.05);
  box-shadow: 0 15px 40px rgba(59, 130, 246, 0.3);
  border-color: rgba(59, 130, 246, 0.4);
  background: rgba(59, 130, 246, 0.15);
}

.category-tag.active {
  background: linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899);
  background-size: 200% 200%;
  animation: gradientText 3s ease-in-out infinite;
  color: white;
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: 0 12px 35px rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(58, 142, 230, 0.3);
}

.tag-indicator {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 3px;
  background: linear-gradient(45deg, #3a8ee6, #42b983);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.category-tag.active .tag-indicator {
  width: 80%;
}

/* 现代化搜索区域样式 */
.search-section {
  margin-bottom: 30px;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  max-width: 600px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 35px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1),
              0 0 0 1px rgba(255, 255, 255, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.4);
  border: 2px solid rgba(59, 130, 246, 0.2);
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  overflow: hidden;
}

.search-input-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.02) 0%,
    rgba(139, 92, 246, 0.04) 50%,
    rgba(236, 72, 153, 0.02) 100%);
  pointer-events: none;
}

.search-input-wrapper:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 20px 60px rgba(59, 130, 246, 0.3);
  transform: translateY(-2px);
}

.search-input {
  flex: 1;
  padding: 18px 30px;
  border: none;
  outline: none;
  font-size: 1.1rem;
  background: transparent;
  border-radius: 35px;
  font-weight: 500;
  position: relative;
  z-index: 2;
}

.search-input::placeholder {
  color: #94a3b8;
  font-weight: 400;
}

.search-btn {
  background: linear-gradient(135deg, #3a8ee6, #42b983);
  border: none;
  padding: 16px 20px;
  border-radius: 0 30px 30px 0;
  cursor: pointer;
  color: white;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-btn:hover {
  background: linear-gradient(135deg, #2980b9, #27ae60);
  transform: scale(1.05);
}

.category-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 0.9rem;
  margin-top: 15px;
}

.loading-icon-sm {
  animation: spin 1s linear infinite;
}

/* 震撼产品内容区域 */
.product-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 60px 20px;
  background: transparent;
  position: relative;
  z-index: 2;
}

/* 现代化产品网格 */
.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 40px;
  margin-bottom: 60px;
  perspective: 1000px;
}

.product-item {
  background: rgba(255, 255, 255, 0.98);
  border-radius: 30px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12),
              0 0 0 1px rgba(255, 255, 255, 0.4),
              inset 0 1px 0 rgba(255, 255, 255, 0.6);
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  position: relative;
  transform-style: preserve-3d;
  animation: productCardFloat 8s ease-in-out infinite;
}

.product-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.02) 0%,
    rgba(139, 92, 246, 0.04) 25%,
    rgba(236, 72, 153, 0.02) 50%,
    rgba(251, 191, 36, 0.04) 75%,
    rgba(34, 197, 94, 0.02) 100%);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.product-item::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(from 0deg, transparent, rgba(59, 130, 246, 0.01), transparent);
  animation: rotate 15s linear infinite;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.product-item:hover {
  transform: translateY(-20px) rotateX(5deg) rotateY(5deg) scale(1.02);
  box-shadow: 0 40px 100px rgba(59, 130, 246, 0.25);
}

.product-item:hover::before,
.product-item:hover::after {
  opacity: 1;
}

.product-image-container {
  height: 250px;
  position: relative;
  overflow: hidden;
  border-radius: 25px 25px 0 0;
}

.product-image-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.8s cubic-bezier(0.23, 1, 0.32, 1);
  filter: brightness(0.9) contrast(1.1) saturate(1.2);
}

.product-item:hover .product-image-container img {
  transform: scale(1.15) rotate(2deg);
  filter: brightness(1.1) contrast(1.2) saturate(1.4);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    rgba(58, 142, 230, 0.8) 0%,
    rgba(66, 185, 131, 0.8) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.product-item:hover .image-overlay {
  opacity: 1;
}

.overlay-content {
  text-align: center;
}

.quick-view-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.quick-view-btn:hover {
  transform: scale(1.1);
}

.product-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: #3a8ee6;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.product-content {
  padding: 30px;
  position: relative;
  z-index: 2;
}

.product-header {
  margin-bottom: 25px;
}

.product-title {
  font-size: 1.6rem;
  font-weight: 800;
  background: linear-gradient(135deg, #2c3e50 0%, #3b82f6 50%, #8b5cf6 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin: 0;
  line-height: 1.3;
  margin-bottom: 10px;
}

.product-details {
  margin-bottom: 30px;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 18px;
  padding: 15px;
  background: rgba(59, 130, 246, 0.06);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  border-left: 4px solid #3b82f6;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.detail-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.02) 0%,
    rgba(139, 92, 246, 0.04) 50%,
    rgba(236, 72, 153, 0.02) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.detail-item:hover::before {
  opacity: 1;
}

.detail-item:hover {
  transform: translateX(5px);
  border-left-color: #8b5cf6;
}

.detail-item.description-item {
  background: rgba(66, 185, 131, 0.04);
  border-left-color: #42b983;
}

.detail-icon {
  color: #3a8ee6;
  margin-top: 2px;
  flex-shrink: 0;
}

.description-item .detail-icon {
  color: #42b983;
}

.detail-content {
  flex: 1;
}

.detail-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
  display: block;
}

.detail-value {
  font-size: 0.95rem;
  color: #334155;
  line-height: 1.5;
}

.description-item .detail-value {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-actions {
  display: flex;
  justify-content: flex-end;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #3a8ee6, #42b983);
  color: white;
  padding: 12px 24px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(58, 142, 230, 0.2);
}

.action-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(58, 142, 230, 0.3);
}

.action-button svg {
  transition: transform 0.3s ease;
}

.action-button:hover svg {
  transform: translateX(4px);
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .header-content {
    padding: 0 15px;
  }

  .title-section h1 {
    font-size: 2.8rem;
  }

  .filter-container {
    padding: 30px 15px;
  }

  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 25px;
  }
}

@media (max-width: 768px) {
  .page-header {
    margin-top: 60px;
  }

  .header-background {
    padding: 60px 0 40px;
  }

  .title-section h1 {
    font-size: 2.2rem;
  }

  .title-section p {
    font-size: 1.1rem;
  }

  .filter-container {
    padding: 25px 15px;
  }

  .category-tags {
    justify-content: center;
  }

  .category-tag {
    padding: 10px 20px;
    font-size: 0.9rem;
  }

  .search-input-wrapper {
    max-width: 100%;
  }

  .products-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .product-content {
    padding: 30px 15px;
  }

  .detail-item {
    flex-direction: column;
    gap: 8px;
  }

  .detail-icon {
    align-self: flex-start;
  }
}

/* 加载和错误状态样式 */
.loading-container,
.error-container,
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  margin: 50px 0;
}

.loading-icon,
.error-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.loading-icon {
  color: #3a8ee6;
  animation: rotate 2s linear infinite;
}

.error-icon {
  color: #f56c6c;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes productCenterGradient {
  0%, 100% {
    background-position: 0% 50%;
  }
  25% {
    background-position: 100% 50%;
  }
  50% {
    background-position: 100% 100%;
  }
  75% {
    background-position: 0% 100%;
  }
}

@keyframes headerGradient {
  0%, 100% {
    background-position: 0% 50%;
  }
  33% {
    background-position: 100% 50%;
  }
  66% {
    background-position: 50% 100%;
  }
}

@keyframes pulseGlow {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.05);
  }
}

@keyframes textShine {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes gradientText {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes productCardFloat {
  0%, 100% {
    transform: translateY(0px) rotateX(0deg) rotateY(0deg);
  }
  25% {
    transform: translateY(-3px) rotateX(1deg) rotateY(0.5deg);
  }
  50% {
    transform: translateY(0px) rotateX(0deg) rotateY(-0.5deg);
  }
  75% {
    transform: translateY(2px) rotateX(-0.5deg) rotateY(0.5deg);
  }
}

.retry-btn {
  margin-top: 20px;
  background-color: #3a8ee6;
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
}

.retry-btn:hover {
  background-color: #2c6eb9;
  transform: translateY(-3px);
}

.pagination-container {
  margin-top: 50px;
  display: flex;
  justify-content: center;
}

/* 现代化响应式设计 */
@media (max-width: 1200px) {
  .products-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
  }

  .header-background {
    padding: 100px 0 60px;
  }

  .title-section h1 {
    font-size: 3.5rem;
  }
}

@media (max-width: 992px) {
  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }

  .filter-container {
    padding: 40px 20px;
  }

  .category-tags {
    gap: 12px;
  }

  .search-input-wrapper {
    max-width: 100%;
  }

  .title-section h1 {
    font-size: 3rem;
  }

  .title-section p {
    font-size: 1.4rem;
  }
}

@media (max-width: 768px) {
  .products-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .header-background {
    padding: 80px 0 50px;
  }

  .title-section h1 {
    font-size: 2.5rem;
  }

  .title-section p {
    font-size: 1.2rem;
  }

  .category-tags {
    justify-content: center;
  }

  .category-tag {
    padding: 12px 20px;
  }

  .product-content {
    padding: 25px;
  }

  .product-image-container {
    height: 200px;
  }
}

/* 小型加载指示器 */
.small-loading {
  display: flex;
  align-items: center;
  margin-top: 10px;
  font-size: 0.85rem;
  color: #3a8ee6;
}

.loading-icon-sm {
  font-size: 16px;
  margin-right: 5px;
  animation: rotate 2s linear infinite;
}
</style> 